// userDataCacheService.js - Centralized user data caching and loading service
import { auth, db } from '../firebaseConfig.js';
import { 
    getPlayerBaseline, 
    getPlayerLevelBests, 
    calculatePlayerImprovement,
    getRecentSessionStats,
    getRecentLevelPerformances 
} from './analyticsService.js';

class UserDataCacheService {
    constructor() {
        this.cache = new Map();
        this.loadingPromises = new Map(); // Prevent duplicate requests
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache (increased for better performance)
        
        // Performance monitoring
        this.cacheHits = 0;
        this.cacheMisses = 0;
        this.totalQueries = 0;
        
        console.log('🗄️ UserDataCacheService initialized with 5-minute intelligent caching');
    }
    
    // Get cache key for user data
    getCacheKey(userId, dataType) {
        return `${userId}_${dataType}`;
    }
    
    // Check if cached data is still valid
    isCacheValid(cacheEntry) {
        if (!cacheEntry) return false;
        const now = Date.now();
        return (now - cacheEntry.timestamp) < this.cacheTimeout;
    }
    
    // Get cached data if valid with performance tracking
    getCachedData(userId, dataType) {
        const cacheKey = this.getCacheKey(userId, dataType);
        const cacheEntry = this.cache.get(cacheKey);
        
        this.totalQueries++;
        
        if (this.isCacheValid(cacheEntry)) {
            this.cacheHits++;
            const hitRate = ((this.cacheHits / this.totalQueries) * 100).toFixed(1);
            console.log(`🗄️ ✅ Cache HIT: ${dataType} for user ${userId} (Hit rate: ${hitRate}%)`);
            return cacheEntry.data;
        }
        
        this.cacheMisses++;
        console.log(`🗄️ ❌ Cache MISS: ${dataType} for user ${userId} - loading fresh data`);
        return null;
    }
    
    // Set cached data
    setCachedData(userId, dataType, data) {
        const cacheKey = this.getCacheKey(userId, dataType);
        this.cache.set(cacheKey, {
            data: data,
            timestamp: Date.now()
        });
        
        console.log(`🗄️ Cache SET: ${dataType} for user ${userId}`);
    }
    
    // Clear cache for user (useful for user switching)
    clearUserCache(userId) {
        const keysToDelete = Array.from(this.cache.keys()).filter(key => key.startsWith(userId));
        keysToDelete.forEach(key => this.cache.delete(key));
        console.log(`🗄️ Cache CLEARED for user ${userId}: ${keysToDelete.length} entries`);
    }
    
    // Main method: Get complete user analytics data
    async getUserAnalyticsData(userId, forceRefresh = false) {
        if (!userId) {
            console.warn('🗄️ No userId provided to getUserAnalyticsData');
            return this.getEmptyUserData();
        }
        
        const cacheKey = `analytics_${userId}`;
        
        // Check cache first (unless force refresh)
        if (!forceRefresh) {
            const cached = this.getCachedData(userId, 'analytics');
            if (cached) {
                return cached;
            }
        }
        
        // Check if already loading to prevent duplicate requests
        if (this.loadingPromises.has(cacheKey)) {
            console.log(`🗄️ Already loading analytics for ${userId}, waiting for existing promise`);
            return await this.loadingPromises.get(cacheKey);
        }
        
        // Create loading promise
        const loadingPromise = this.loadFreshUserAnalytics(userId);
        this.loadingPromises.set(cacheKey, loadingPromise);
        
        try {
            const data = await loadingPromise;
            this.setCachedData(userId, 'analytics', data);
            return data;
        } finally {
            // Clean up loading promise
            this.loadingPromises.delete(cacheKey);
        }
    }
    
    // Load fresh analytics data from Firebase
    async loadFreshUserAnalytics(userId) {
        console.log(`🗄️ Loading FRESH analytics data for user: ${userId}`);
        
        try {
            // Wait for authentication to be ready
            const user = await this.waitForAuthReady(userId);
            if (!user) {
                console.warn('🗄️ User not authenticated, returning empty data');
                return this.getEmptyUserData();
            }
            
            // Load profile data
            const profileData = await this.loadUserProfile(userId);
            
            // Load PPT Hero analytics using CORRECT collections
            const [baseline, levelBests, improvement, recentStats, recentPerformances, scores] = await Promise.all([
                getPlayerBaseline(userId).catch(e => {
                    console.warn('🗄️ Baseline load failed:', e.code);
                    return null;
                }),
                getPlayerLevelBests(userId, "PPTHero").catch(e => {
                    console.warn('🗄️ Level bests load failed:', e.code);
                    return [];
                }),
                calculatePlayerImprovement(userId, "PPTHero").catch(e => {
                    console.warn('🗄️ Improvement calc failed:', e.code);
                    return null;
                }),
                getRecentSessionStats(userId, "PPTHero").catch(e => {
                    console.warn('🗄️ Session stats load failed:', e.code);
                    return null;
                }),
                getRecentLevelPerformances(userId, "PPTHero").catch(e => {
                    console.warn('🗄️ Recent performances load failed:', e.code);
                    return [];
                }),
                this.loadScoresFromCorrectCollection(userId).catch(e => {
                    console.warn('🗄️ Scores load failed:', e.code);
                    return [];
                })
            ]);
            
            // Calculate derived metrics
            const pptBestScore = Math.max(...(scores || []).map(s => s.score || 0), 0);
            const pptGamesPlayed = (levelBests || []).length;
            const pptHighestLevel = Math.max(...(levelBests || []).map(l => l.levelNumber || 1), 1);
            const avgScore = scores && scores.length > 0 ? scores.reduce((sum, s) => sum + (s.score || 0), 0) / scores.length : 0;
            
            const analyticsData = {
                profileData: profileData,
                pptHero: {
                    baseline: baseline,
                    levelBests: levelBests || [],
                    improvement: improvement,
                    recentStats: recentStats,
                    recentPerformances: recentPerformances || [],
                    scores: scores || [],
                    bestScore: pptBestScore,
                    gamesPlayed: pptGamesPlayed,
                    highestLevel: pptHighestLevel,
                    averageScore: avgScore
                },
                classifyTech: { scores: [] },
                shortcuts: { scores: [] },
                loadedAt: Date.now(),
                source: 'UserDataCacheService'
            };
            
            console.log('🗄️ FRESH analytics data loaded successfully:', {
                userId,
                hasBaseline: !!baseline,
                levelBests: levelBests?.length || 0,
                scores: scores?.length || 0,
                bestScore: pptBestScore
            });
            
            return analyticsData;
            
        } catch (error) {
            console.error('🗄️ Error loading fresh analytics data:', error);
            
            // Try localStorage as ultimate fallback
            return await this.loadFromLocalStorageFallback(userId);
        }
    }
    
    // Wait for authentication to be fully ready
    async waitForAuthReady(expectedUserId, maxWait = 5000) {
        return new Promise((resolve) => {
            const startTime = Date.now();
            
            const checkAuth = () => {
                const user = auth.currentUser;
                
                if (user && user.uid === expectedUserId) {
                    console.log('🗄️ Authentication ready for:', expectedUserId);
                    resolve(user);
                    return;
                }
                
                // Check if we've waited too long
                if ((Date.now() - startTime) > maxWait) {
                    console.warn('🗄️ Authentication timeout after', maxWait, 'ms');
                    resolve(null);
                    return;
                }
                
                // Check again in 100ms
                setTimeout(checkAuth, 100);
            };
            
            checkAuth();
        });
    }
    
    // Load user profile data
    async loadUserProfile(userId) {
        try {
            const profileDoc = await db.collection("userProfiles").doc(userId).get();
            
            if (profileDoc.exists) {
                const profile = profileDoc.data();
                console.log('🗄️ Profile loaded from Firebase');
                return profile;
            } else {
                console.warn('🗄️ No profile document found');
                return {
                    displayName: 'Executive User',
                    email: auth.currentUser?.email || 'user@example.com'
                };
            }
        } catch (error) {
            console.error('🗄️ Profile loading failed:', error);
            return {
                displayName: 'Executive User',
                email: auth.currentUser?.email || 'user@example.com'
            };
        }
    }
    
    // Load scores from correct collection with optimized query
    async loadScoresFromCorrectCollection(userId) {
        try {
            console.log(`🗄️ Loading scores with OPTIMIZED composite index query for user: ${userId}`);
            
            // OPTIMIZED: Use the composite index (userUID + game + score) for maximum performance
            const snapshot = await db.collection("scores")
                .where("userUID", "==", userId)
                .where("game", "==", "PPTHero")
                .orderBy("score", "desc")
                .limit(20)
                .get();
                
            const scores = [];
            snapshot.forEach(doc => {
                scores.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            
            console.log(`🗄️ ✅ OPTIMIZED QUERY: Loaded ${scores.length} PPT Hero scores using composite index`);
            return scores;
            
        } catch (error) {
            console.error('🗄️ Error in optimized scores query:', error);
            
            // Fallback to simpler query if composite index isn't ready yet
            try {
                console.log('🗄️ Attempting fallback query without ordering...');
                const fallbackSnapshot = await db.collection("scores")
                    .where("userUID", "==", userId)
                    .where("game", "==", "PPTHero")
                    .limit(20)
                    .get();
                    
                const scores = [];
                fallbackSnapshot.forEach(doc => {
                    scores.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });
                
                // Sort client-side as fallback
                scores.sort((a, b) => (b.score || 0) - (a.score || 0));
                
                console.log(`🗄️ Fallback query successful: ${scores.length} scores loaded`);
                return scores;
                
            } catch (fallbackError) {
                console.error('🗄️ Both optimized and fallback queries failed:', fallbackError);
                return [];
            }
        }
    }
    
    // Load from localStorage as ultimate fallback
    async loadFromLocalStorageFallback(userId) {
        try {
            console.log('🗄️ Using localStorage fallback for:', userId);
            
            const userSpecificKey = `pptHeroUnifiedScores_${userId}`;
            const localScores = localStorage.getItem(userSpecificKey);
            
            if (localScores) {
                const parsedScores = JSON.parse(localScores);
                
                const fallbackData = {
                    profileData: {
                        displayName: auth.currentUser?.displayName || 'Cached User',
                        email: auth.currentUser?.email
                    },
                    pptHero: {
                        scores: Object.entries(parsedScores).map(([roundId, roundData]) => ({
                            score: roundData.bestScore || 0,
                            round: roundId,
                            userUID: userId,
                            game: 'PPTHero',
                            player: 'Cached User'
                        })),
                        bestScore: Math.max(...Object.values(parsedScores).map(r => r.bestScore || 0), 0),
                        gamesPlayed: Object.values(parsedScores).filter(r => r.completed).length
                    },
                    loadedAt: Date.now(),
                    source: 'localStorage_fallback'
                };
                
                console.log('🗄️ Fallback data prepared from localStorage');
                return fallbackData;
            }
        } catch (error) {
            console.error('🗄️ localStorage fallback failed:', error);
        }
        
        return this.getEmptyUserData();
    }
    
    // Get empty data structure
    getEmptyUserData() {
        return {
            profileData: {
                displayName: 'Executive User',
                email: 'user@example.com'
            },
            pptHero: {
                baseline: null,
                levelBests: [],
                scores: [],
                bestScore: 0,
                gamesPlayed: 0,
                averageScore: 0
            },
            classifyTech: { scores: [] },
            shortcuts: { scores: [] },
            loadedAt: Date.now(),
            source: 'empty_fallback'
        };
    }
    
    // Clear all cache (useful for user switching)
    clearAllCache() {
        this.cache.clear();
        this.loadingPromises.clear();
        console.log('🗄️ All cache cleared');
    }
    
    // Get cache statistics for debugging with performance metrics
    getCacheStats() {
        const hitRate = this.totalQueries > 0 ? ((this.cacheHits / this.totalQueries) * 100).toFixed(1) : 0;
        
        return {
            totalEntries: this.cache.size,
            activeLoading: this.loadingPromises.size,
            entries: Array.from(this.cache.keys()),
            performance: {
                totalQueries: this.totalQueries,
                cacheHits: this.cacheHits,
                cacheMisses: this.cacheMisses,
                hitRate: `${hitRate}%`,
                cacheTimeoutMinutes: this.cacheTimeout / (60 * 1000)
            }
        };
    }
    
    // Reset performance metrics
    resetPerformanceMetrics() {
        this.cacheHits = 0;
        this.cacheMisses = 0;
        this.totalQueries = 0;
        console.log('🗄️ Performance metrics reset');
    }
}

// Create singleton instance
const userDataCacheService = new UserDataCacheService();

// Global debug helper
window.debugUserDataCache = function() {
    console.log('🗄️ === USER DATA CACHE DEBUG ===');
    console.log('Cache stats:', userDataCacheService.getCacheStats());
    console.log('Current user:', auth.currentUser?.uid || 'None');
    console.log('================================');
};

// Clear cache on user switch and detect user switching
let previousUserId = null;

auth.onAuthStateChanged((user) => {
    const currentUserId = user?.uid || null;
    
    if (previousUserId && previousUserId !== currentUserId) {
        console.log(`🗄️ User switching detected: ${previousUserId} → ${currentUserId}`);
        userDataCacheService.clearAllCache();
    } else if (!user) {
        console.log('🗄️ User signed out, clearing cache');
        userDataCacheService.clearAllCache();
    }
    
    previousUserId = currentUserId;
});

// Enhanced debug tools
window.forceRefreshUserData = async function(userId) {
    if (!userId && auth.currentUser) {
        userId = auth.currentUser.uid;
    }
    
    if (!userId) {
        console.error('No user ID provided or available');
        return;
    }
    
    console.log('🔄 Force refreshing user data for:', userId);
    userDataCacheService.clearUserCache(userId);
    const freshData = await userDataCacheService.getUserAnalyticsData(userId, true);
    console.log('🔄 Fresh data loaded:', freshData);
    return freshData;
};

export default userDataCacheService;
